'use client';

import React, { useEffect, useState } from 'react';
import { MobileNotice } from '@/components/mobile-notice';
import { isMobileDevice } from '@/lib/is-mobile';
import { usePathname } from 'next/navigation';
import { i18n } from '@/i18n';

/**
 * 移动端文案配置接口
 */
export interface MobileMessageConfig {
  /** 提示文案 */
  message: string;
  /** 按钮文案 */
  buttonText: string;
}

/**
 * 默认移动端文案配置
 */
export const DEFAULT_MOBILE_MESSAGE: MobileMessageConfig = {
  message: i18n.t('components:mobileNotice.mobileDevelopment'),
  buttonText: i18n.t('components:mobileNotice.useOnPc'),
};

/**
 * 允许在移动设备上访问的路由前缀列表
 * 如果URL以这些前缀开头，即使在移动设备上也会显示内容
 */
export const ALLOWED_MOBILE_ROUTES = [
  '/report', // 报告相关路由
  '/share', // 分享相关路由
  '/404', // 404页面
  '/auth', // 登录注册相关路由
  // 可以在这里添加更多允许的路由前缀
];

/**
 * 路由分组配置接口
 */
export interface RouteGroupConfig {
  routes: string[];
  messageConfig: MobileMessageConfig;
}

/**
 * 移动端路由分组配置
 * 每个组包含一组路由和对应的文案配置
 */
export const MOBILE_ROUTE_GROUPS: RouteGroupConfig[] = [
  {
    routes: ['/space-manage', 'space-expire'],
    messageConfig: {
      message: i18n.t('components:mobileNotice.adminUseOnPc'),
      buttonText: i18n.t('components:mobileNotice.copyLinkToPc'),
    },
  },
];

/**
 * 检查给定的路径是否属于允许在移动设备上访问的路由
 * @param pathname 当前路径
 * @returns 如果路径允许在移动设备上访问则返回true，否则返回false
 */
export function isAllowedOnMobile(pathname: string): boolean {
  return ALLOWED_MOBILE_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * 根据路径获取对应的移动端文案配置
 * @param pathname 当前路径
 * @returns 返回对应的文案配置，如果没有匹配的路由则返回默认配置
 */
export function getMobileMessageConfig(pathname: string): MobileMessageConfig {
  for (const group of MOBILE_ROUTE_GROUPS) {
    if (group.routes.some((route) => pathname.startsWith(route))) {
      return group.messageConfig;
    }
  }
  return DEFAULT_MOBILE_MESSAGE;
}

interface MobileDetectorProps {
  children: React.ReactNode;
}

export function MobileDetector({ children }: MobileDetectorProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isAllowedRoute, setIsAllowedRoute] = useState(false);
  const [messageConfig, setMessageConfig] = useState<MobileMessageConfig>(
    DEFAULT_MOBILE_MESSAGE,
  );

  // 检查当前URL是否属于允许在移动设备上访问的路由
  const pathname = usePathname();

  // 确保组件在客户端渲染并检测移动设备和路由
  useEffect(() => {
    setMounted(true);
    setIsMobile(isMobileDevice());
    setIsAllowedRoute(isAllowedOnMobile(pathname));
    setMessageConfig(getMobileMessageConfig(pathname));
  }, [pathname]);

  useEffect(() => {
    const setVH = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`,
      );
    };
    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  // 如果组件尚未挂载，返回null以避免服务器/客户端不匹配
  if (!mounted) {
    return null;
  }

  // 如果是移动设备但不是允许的路由，显示移动设备提示
  // 如果是允许的路由，即使在移动设备上也显示内容
  if (isMobile && !isAllowedRoute) {
    return <MobileNotice messageConfig={messageConfig} />;
  }

  return <>{children}</>;
}
